import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { DragDropContext } from '@hello-pangea/dnd';
import KanbanColumn from './KanbanColumn';
import { PIPELINE_STAGES, getStageForStatus, getIdleUrgency } from './pipelineConfig';

// Sort priority: red=0 (first), yellow=1, green=2 (last)
const urgencySortOrder = { red: 0, yellow: 1, green: 2 };

const KanbanBoard = ({ leads, onStatusChange, loading }) => {
  // Deferred rendering: show a loading spinner on initial mount so the UI stays responsive
  // Only defers the FIRST render — subsequent data updates (status changes) render immediately
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!ready) {
      const raf = requestAnimationFrame(() => {
        setReady(true);
      });
      return () => cancelAnimationFrame(raf);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Group leads into columns by pipeline stage
  const columns = useMemo(() => {
    const grouped = {};
    PIPELINE_STAGES.forEach((stage) => {
      grouped[stage.id] = [];
    });

    (leads || []).forEach((lead) => {
      const stage = getStageForStatus(lead.leadStatus || 'New');
      if (grouped[stage.id]) {
        grouped[stage.id].push(lead);
      } else {
        grouped.new.push(lead);
      }
    });

    // Sort within each column: urgent (red) first, then yellow, then green
    Object.keys(grouped).forEach((stageId) => {
      grouped[stageId].sort((a, b) => {
        const urgA = urgencySortOrder[getIdleUrgency(a.lastActionDate)] ?? 1;
        const urgB = urgencySortOrder[getIdleUrgency(b.lastActionDate)] ?? 1;
        return urgA - urgB;
      });
    });

    return grouped;
  }, [leads]);

  const handleDragEnd = useCallback((result) => {
    const { destination, source, draggableId } = result;

    // Dropped outside a column
    if (!destination) return;

    // Dropped in same position
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    // Dropped in a different column -> status change
    if (destination.droppableId !== source.droppableId) {
      const targetStage = PIPELINE_STAGES.find((s) => s.id === destination.droppableId);
      const sourceStage = PIPELINE_STAGES.find((s) => s.id === source.droppableId);

      if (targetStage && sourceStage) {
        // Default to the first (primary) status in the target stage
        const defaultTargetStatus = targetStage.statuses[0];

        onStatusChange({
          leadId: draggableId,
          newStatus: defaultTargetStatus,
          sourceStage,
          targetStage,
        });
      }
    }
  }, [onStatusChange]);

  if (loading || !ready) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Loading pipeline view...
        </Typography>
      </Box>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          pb: 2,
          minHeight: 'calc(100vh - 250px)',
          width: '100%',
        }}
      >
        {PIPELINE_STAGES.map((stage) => (
          <KanbanColumn key={stage.id} stage={stage} leads={columns[stage.id] || []} />
        ))}
      </Box>
    </DragDropContext>
  );
};

export default React.memo(KanbanBoard);
